import React from 'react';
import { Motion, spring } from 'react-motion';
import get from 'lodash/get';
import isEqual from 'lodash/isEqual';
import queryString from 'query-string';

import Browser from '../util/Browser';
import Viewer from './Viewer';
import Moat from '../util/Moat';

const onErrorEventType = 'error'; // this var exists for testing

class CardView extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      loadingJson: true,
      openCeltra: false,
      width: get(this.props, 'ad.width', 0),
      height: get(this.props, 'ad.height', 0),
      paddingTop: 0,
      maxHeight: 'auto',
      maxWidth: 'auto',
      frameScale: 'scale(0)',
      marginBottom: 0,
      borderWidth: 0,
      borderColor: '#ffffff',
      left: 0,
      click_through: false,
      percentViewable: 0,

      mouse: [0, 0],
      isPressed: false,
      springConfig: [300, 40],
      finalPositionX: 0,
      wrapperDisplay: 'block',
      wrapperHeight: '100%',
      hostedInhouse: false,
      preview: !!Browser.getParameterValue('preview_in_browser'),
      cardTransitionDuration: '400ms',
    };

    this.cardIframe = React.createRef();
  }

  componentWillMount() {
    window.addEventListener('message', event => {
      // CMP Ads in DFP rendered in a safeframe don't have all the inMarketParams we have here,
      // so send them in in response to 3rd party impr pels being loaded
      if (event && event.data && typeof event.data === 'string') {
        try {
          const eventData = JSON.parse(event.data);
          if (eventData.action && eventData.action === 'iframe_loaded') {
            this.setState({ thirdParPelSrc: event.source });
          }
        } catch (e) {}
      }
    });

    window.server.log({
      tag: 'Card_will_mount',
      inmarketParams: { m2m_impr_id: this.props.ad.impression_id },
    });

    window.addEventListener('message', this.postMessageReceiver);
  }

  componentDidMount() {
    const { ad, componentName } = this.props;
    const { impression_id } = ad;

    window.server.log({
      tag: 'Card_mounted',
      inmarketParams: { m2m_impr_id: impression_id },
    });

    /**
     * Iframe loading and pixel firing variables.
     */
    // Used in internal pixel firing logic.
    this.firedPixels = false;
    this.thirdPartyPixelFired = false;
    // Used to prevent handlePixelFire from executing multiple times.
    this.firePixelExec = false;
    // Used to detect Card loading errors.
    this.iframeLoaded = false;
    this.iframeError = false;

    if (!this.state.iframeSrc && get(ad, 'html')) {
      let html = get(ad, 'html');
      try {
        html = atob(get(ad, 'html'));
      } catch (e) {}

      this.setState(
        {
          loadingJson: false,
          hostedInhouse: true,
          iframeContents: {
            type: 'html',
            immediateRender: 1,
            thirdPartyPels: '',
            click_through_url: '',
            creative_url: '',
            url: ad.url || '',
            interstitial_html: html,
            pixels: '',
            scriptHost: '',
            noPixel: false,
          },
        },
        () => {
          if (Browser.isIOS()) {
            // this is because sometimes ios gets stuck...
            // there is also a corresponding NSTimer that js evals 1+1
            // to keep the webview from going idle during preload.
            setTimeout(() => {
              if (!this.iframeLoaded) {
                console.log('ios specific iframe load timeout firing');
                this.iframeLoaded = true;
                this.didFinishLoad(null);
              }
            }, 1000);
          }
        }
      );

      return;
    }

    if (!this.state.iframeSrc && get(ad, 'url')) {
      const hostedInhouse = Browser.getHost(ad.url) === Browser.getHost(null);

      // Adding extra parameters to mark this an e2 ad
      let iframeSrc = ad.url;
      const params = queryString.parseUrl(iframeSrc);
      params.query.device = window.server.device;
      params.query.sessionId = window.server.sessionId;
      params.query.componentName = componentName;
      params.query.e2Version = Browser.getParameterValue('v');
      if (!params.query.m2m_ad_is_e2) {
        params.query.m2m_ad_is_e2 = 1;
      }
      const paramsExtra = queryString.stringify(params.query);
      iframeSrc = `${params.url}?${paramsExtra}`;

      this.getFrameContents(iframeSrc);

      if (Browser.isIOS() && iframeSrc && iframeSrc.length > 10) {
        // this is because sometimes ios gets stuck...
        // there is also a corresponding NSTimer that js evals 1+1
        // to keep the webview from going idle during preload.
        setTimeout(() => {
          if (!this.iframeLoaded) {
            console.log('ios specific iframe load timeout firing');
            this.iframeLoaded = true;
            this.didFinishLoad(null);
          }
        }, 1000);
      }

      this.setState({ iframeSrc, hostedInhouse });
    }
  }

  shouldComponentUpdate(nextProps, nextState, nextContext) {
    if (!this.props.cardViewState && nextProps.cardViewState) {
      this.setState({ ...nextProps.cardViewState });
    }

    return true;
  }

  componentDidUpdate(prevProps, prevState) {
    if (this.state.thirdParPelSrc) {
      this.handlePixelFire();
    }

    if (
      prevState.frameScale !== this.state.frameScale ||
      prevState.left !== this.state.left ||
      prevState.width !== this.state.width
    ) {
      this.postE2IFrameScale('sizeChange', []);
      if (this.state.thirdPartyImprPelTarget) {
        this.postE2IFrameScale('targetChange', [this.state.thirdPartyImprPelTarget]);
      }
    }

    // Found third party impl !
    if (prevState.thirdPartyImprPelTarget !== this.state.thirdPartyImprPelTarget) {
      this.postE2IFrameScale('targetChange', [this.state.thirdPartyImprPelTarget]);
    }
  }

  getFrameContents = iframeSrc => {
    // Only allow creative images for now
    const params = queryString.parseUrl(iframeSrc);
    params.query.json = true;
    const paramsWithJson = queryString.stringify(params.query);
    const withJsonUrl = `${params.url}?${paramsWithJson}`;

    if (!this.props.fetchJson) {
      console.log('card.getFrameContents - Not attempting a fetch, as fetchJson was not true');

      if (
        Array.isArray(this.props.ad.tracking_pixels) &&
        this.props.ad.tracking_pixels.join(',').includes('sizmek')
      ) {
        console.log('card.getFrameContents - This is sizmek, so setting as a preview ad to prevent E2 from closing');
        this.setState({ preview: true, loadingJson: false });
      } else {
        this.setState({ loadingJson: false });
      }
      return;
    }

    if (Browser.getParameterValue('preview_in_browser')) {
      console.log('card.getFrameContents - Setting iframeContents manually as this is a preview');
      let html = '';
      try {
        html = atob(Browser.getParameterValue('interstitial_html'));
      } catch (e) {}
      this.setState({
        preview: true,
        loadingJson: false,
        iframeContents: {
          type: Browser.getParameterValue('creative_type'),
          immediateRender: 1,
          thirdPartyPels: '',
          click_through_url: Browser.getParameterValue('click_through_url'),
          creative_url: Browser.getParameterValue('ad'),
          interstitial_html: html,
          pixels: '',
          scriptHost: '',
          noPixel: false,
        },
      });
      return;
    }

    if (
      !withJsonUrl ||
      withJsonUrl.indexOf('%2Fconnections%2Fdfp') !== -1 ||
      withJsonUrl.indexOf('/connections/dfp/') !== -1 ||
      withJsonUrl.indexOf('surveygizmo') !== -1
    ) {
      console.log(
        "card.getFrameContents - Not attempting a fetch, as it's a DFP ad or unknown url",
        withJsonUrl,
        paramsWithJson
      );
      this.setState({ loadingJson: false });
      return;
    }

    console.log('card.getFrameContents - Grabbing contents of creative frame', {
      params,
      iframeSrc,
      withJsonUrl,
    });

    fetch(withJsonUrl)
      .then(r => r.text())
      .then(iframeContents => {
        try {
          let parsedFrameContents = JSON.parse(iframeContents);
          if (parsedFrameContents) {
            console.log('card.getFrameContents - iFrame contents success', parsedFrameContents);
            let { preview } = this.state;
            if (
              get(parsedFrameContents, 'type') === 'cmp' &&
              get(parsedFrameContents, 'interstitial_html', '').indexOf('serving-sys') !== -1
            ) {
              preview = true;
              console.log("card.getFrameContents - Marking this ad as 'preview' so as not to close E2 automatically");
            }
            const trackedPixels = get(this.props, 'ad.tracking_pixel_html');
            parsedFrameContents.pixels = trackedPixels || parsedFrameContents.pixels;
            parsedFrameContents.pixel = trackedPixels || parsedFrameContents.pixels;
            console.log("Pixels setting to: ", parsedFrameContents.pixels);
            this.setState({ iframeContents: parsedFrameContents, preview }, () => {
              if (get(parsedFrameContents, 'error')) {
                const debug = document.getElementById('moatParamsTextArea');
                if (debug) {
                  document.getElementById('moatParamsTextArea').style.background = 'red';
                  document.getElementById('moatParamsTextArea').value = 'iFrame error';
                  document.getElementById('moatParamsTextArea').style.color = 'white';
                }
                this.iframeError = true;
                this.handleIframeError();
              }
            });
          }
        } catch (e) {
          console.log('card.getFrameContents - Could not grab creative frame, not a json response?', e);
        }
      })
      .catch(e => console.log('card.getFrameContents - Could not grab creative frame, not a json response?', e))
      .finally(() => this.setState({ loadingJson: false }));
  };

  sendAppOpenedEvent = () => {
    // So that Celtra tags will know to load now...
    // can be caught by other types, as well, if needed
    console.log(
      `CardView.sendAppOpenedEvent - firing app_opened into the iframe ${this.props.componentName}`
    );
    const targetIFrame = document.getElementById(this.props.componentName);
    console.log(
      'CardView.sendAppOpenedEvent - firing app_opened into the iframe; targetIFrame: ',
      targetIFrame
    );

    if (!targetIFrame) {
      console.log('CardView.sendAppOpenedEvent - Calling isReadyToOpen()');
      this.setState({ openCeltra: true }, this.props.isReadyToOpen);
      return;
    }

    console.log('CardView.sendAppOpenedEvent - Found targetIFrame, not calling isReadyToOpen manually');
    try {
      targetIFrame.contentWindow.postMessage(
        JSON.stringify({
          action: 'app_opened',
          cardTop: Math.floor(targetIFrame.getBoundingClientRect().top),
          m2m_impr_id: this.props.ad.impression_id,
          componentName: this.props.componentName,
        }),
        '*'
      );
    } catch (e) {
      console.warn(e, 'CardView.sendAppOpenedEvent - Could not send message to iFrame, CORS issue ?', targetIFrame);
    }
  };

  /**
   * didFinishLoad
   * is only used for ads which are served using the sample
   * scheme, domain and port as the webapp
   */
  didFinishLoad = event => {
    console.log(this.iframeLoaded, 'card.didFinishLoad event: ', event);
    if (this.iframeLoaded) {
      console.log(`card.didFinishLoad hostedInhouse${this.state.hostedInhouse.toString()}`);

      this.handlePixelFire();
      this.focusWindow();
      return this.props.didFinishLoad(event);
    }
    /*
     * No post message was received from iframe, wait another 500ms and log error.
     */
    console.log('card.didFinishLoad - No post message was received from iframe, wait another 3s and log error');
    const _event = event;
    this.iframeError = true;
    window.server.log({
      tag: 'IFRAME_LOAD_ERROR',
      msg: 'Iframe failed to load.',
      inmarketParams: {
        componentName: this.props.componentName,
        m2m_impr_id: this.props.ad.impression_id,
        m2m_relsno: this.props.ad.m2m_relsno,
      },
      meta_data: {
        ad_id: Browser.getParameterValue('ad_id', this.props.ad.url) || '',
        open: this.props.open || false,
      },
    });

    // Give it 3s to completely load in order to get pixels from a cross domain
    this.iframeErrorTimeout = setTimeout(() => {
      console.log('card.didFinishLoad - CardView - iframeErrorTimeout fired');
      this.handleIframeError(_event);
      clearInterval(this.iframeErrorRepeatCheck);

      if (Browser.shouldShowMoatDebug()) {
        const msg = document.getElementById('moatParamsTextArea');
        if (msg) {
          msg.value = 'Moat never fired, or third_party_impr_pels.js error';
        }
      }
    }, 3000);

    // Check every 100ms whether the iframe actually fired a pixel
    this.iframeErrorRepeatCheck = setInterval(() => {
      if (!this.iframeError && !this.state.preview) {
        console.log('card.didFinishLoad - The iframe loaded! No errors');

        // Stop checking for errors every 100ms
        clearInterval(this.iframeErrorRepeatCheck);

        // Don't log an error because the iframe ended up loading fine
        clearTimeout(this.iframeErrorTimeout);
      }
    }, 100);

    return this.props.didFinishLoad(event);
  };

  handlePlayVideo = () => {
    console.log('card.handlePlayVideo entered');
    const targetIFrame = document.getElementById(this.props.componentName);
    if (targetIFrame && targetIFrame.contentWindow) {
      targetIFrame.contentWindow.postMessage(
        JSON.stringify({
          action: 'play_video',
          cardTop: this.cardIframe.getBoundingClientRect
            ? Math.floor(this.cardIframe.getBoundingClientRect().top)
            : false,
          m2m_impr_id: this.props.ad.impression_id,
          componentName: this.props.componentName,
        }),
        '*'
      );
    } else {
      console.log('card.handlePlayVideo, iframe not found.');
    }
  };

  logTagLoadError = (fn, tag, evt) => {
    window.server.log({
      fn,
      tag,
      msg: get(evt, 'srcElement.src'),
      origin: get(evt, 'origin'),
      inmarketParams: {
        componentName: this.props.componentName,
        m2m_impr_id: this.props.ad.impression_id,
        m2m_relsno: this.props.ad.m2m_relsno,
      },
      meta_data: {
        ad_id: Browser.getParameterValue('ad_id', this.props.ad.url) || '',
        open: this.props.open || false,
      },
    });
  };

  fireInternalPixel = () => {
    const trackingPixels = [];
    this.props.ad.tracking_pixels
      .join('\n')
      .split('\n')
      .filter(a => a.trim())
      .forEach((pixel, index, array) => {
        if (pixel.indexOf('/advertiser/pixel') === -1) {
          trackingPixels.push(pixel);
        }
      });

    // fire the pixels that were sent along in the payload
    console.log(`card.fireInternalPixel - firing third party impression pixels: `, trackingPixels);
    let pixelHtml = '';
    trackingPixels.forEach((pixel, index, array) => {
      console.log('   pixel', pixel);

      if (Browser.validURL(pixel)) {
        const s = document.createElement('img');
        s.width = 0;
        s.height = 0;
        s.style.cssText = `position: absolute;top: -9999px;left: -9999px;`;
        s.src = pixel;
        s.addEventListener(onErrorEventType, evt => {
          this.logTagLoadError('Card.handlePixelFire', '3RD_IMG_PIXEL_ONERROR', evt);
        });
        pixelHtml += s.innerHTML || s.outerHTML;

        window.server.log({
          fn: 'CardView.handlePixelFire',
          tag: 'SLIDE_ADV_PEL_APPEND',
          msg: 'Calling advertiser/pixel',
          inmarketParams: {
            componentName: this.props.componentName,
            m2m_impr_id: this.props.ad.impression_id,
            m2m_relsno: this.props.ad.m2m_relsno,
          },
        });
      } else {
        pixelHtml += this.handlePixelFireHtml(this, pixel);

        window.server.log({
          fn: 'CardView.handlePixelFire',
          tag: 'SLIDE_ADV_PEL_FAIL',
          msg: 'Browser.validURL() failed',
          pixel,
          inmarketParams: {
            componentName: this.props.componentName,
            m2m_impr_id: this.props.ad.impression_id,
            m2m_relsno: this.props.ad.m2m_relsno,
          },
        });

        console.log('card.fireInternalPixel - Thirdparty pixel fire via javascript eval');
      }
    });

    this.postMessageSender(pixelHtml);
  };

  handlePixelFireHtml = (card, pixel) => {
    let injectThisHTML5 = pixel.replace(/%CA/g, '%25CA'); // SEEMS LIKE THIS IS GOOGLE'S BUG
    console.log(`app.js - injectThisHTML5(in): ${injectThisHTML5}`);
    try {
      injectThisHTML5 = decodeURIComponent(injectThisHTML5);
    } catch (e) {
      // So maybe it's not uri encoded
    }
    // NOW DO REPLACEMENT -- NOTICE WE CAN NOW REPLACE THE "REAL" TAGS WITH %% INSTEAD OF URL ENCODED
    injectThisHTML5 = injectThisHTML5.replace(
      /%%CACHEBUSTER%%/g,
      Math.floor(Math.random() * (99999999 - 1)) + 1
    );
    injectThisHTML5 = injectThisHTML5.replace(
      /%%25CACHEBUSTER%%/g,
      Math.floor(Math.random() * (99999999 - 1)) + 1
    );
    // allow the same field replacements that we have in the third_part_impr_pel.js
    injectThisHTML5 = injectThisHTML5.replace(/%%(?:URLPARAM|PATTERN):(.*?)%%/g, (m, g1) =>
      Browser.getParameterValue(g1, card.props.ad.url)
    );
    console.log(`app.js - injectThisHTML5(final): ${injectThisHTML5}`);
    return injectThisHTML5;
  };

  focusWindow = () => {
    if (!document.hasFocus() && this.state.refocused === false) {
      window.focus();
      this.setState({ refocused: true });
    }
    return true;
  };

  /**
   * If there's an iFrame error, we exit gracefully
   *
   * @param event
   * @returns {boolean}
   */
  handleIframeError = event => {
    console.log('CardView - iframeErrorTimeout fired', this.iframeError, this.state.preview);

    if (this.iframeError === true && !this.state.preview) {
      this.props.didFinishLoad(event, true);
    } else {
      // Invalid error due to caching, continue firing callback.
      this.props.didFinishLoad(event);
    }
    return false;
  };

  /**
   * Check the ad card has slid at least x% into the viewport.
   *
   * Card is loaded and in view:
   1.  E2 is open
   2.  "iframe_loaded" window postmessage has been received
   3.  The pixels have not been fired
   4.  Card has slid into viewport at least 50%, 90% for Android
   */
  handlePixelFire = () => {
    if (!this.cardLoadedInView()) {
      return;
    }

    console.log('cardLoadedInView');
    this.firePixelExec = true;

    // Separate our impression pixel from other third party pixels
    let impressionPixel;
    this.props.ad.tracking_pixels.forEach((pixel, index, array) => {
      if (pixel.indexOf('/advertiser/pixel') !== -1) {
        impressionPixel = pixel;
      }
    });

    if (impressionPixel) {
      // fire the impression pixel
      const s = document.createElement('img');
      s.width = 0;
      s.height = 0;
      s.style.cssText = `position: absolute;top: -9999px;left: -9999px;`;
      s.src = impressionPixel;
      s.addEventListener(onErrorEventType, evt => {
        this.logTagLoadError('Card.handlePixelFire', 'IMPR_PIXEL_ONERROR', evt);
      });
      document.body.appendChild(s);
    }

    // fire the device_impression_pel pixel
    this.fireInternalPixel();

    this.firedPixels = true;

    // send video play message.
    this.handlePlayVideo();
  };

  cardInViewport = () => {
    if (!this.cardIframe.getBoundingClientRect) return false;

    const cardTop = this.cardIframe.getBoundingClientRect().top;
    // iOS the card should be 50% in view.
    if (Browser.isIOS() === true) {
      return !!(cardTop && cardTop < this.state.height / 2);
    }
    /*
     * Android the card should be 90% in view.
     * Since there is no preloading in Android the card pels.js notification and onload fire as the card is sliding up.
     * This may be causing the pixel to fire too early when the ad isn't in view.
     */
    const targetTop =
      this.state.height && this.state.paddingTop
        ? this.state.height * 0.1 + this.state.paddingTop
        : Browser.getHeaderHeight();
    return !!(cardTop && cardTop < targetTop);
  };

  // Post message received after didFinishLoad().
  logIframeErrorMisfire = origin => {
    window.server.log({
      fn: 'Card.postMessageReceiver',
      tag: 'IFRAME_LOAD_ERROR_MISFIRE',
      msg: 'Iframe load error false positive recorded.',
      origin,
      inmarketParams: {
        componentName: this.props.componentName,
        m2m_impr_id: this.props.ad.impression_id,
        m2m_relsno: this.props.ad.m2m_relsno,
      },
      open: this.props.open || false,
    });
    return true;
  };

  /**
   * Once the receiver gets a message from third_party_impr_pels
   * we then know that we are then able to fire our pixel.
   *
   * In the case of the first card, we have a race condition.
   * SliderView will try and fire the impression and pixel when the
   * first card is loaded but the third_party_impr_pels file may not have
   * fully loaded which will cause our tracking pixel call
   * to /advertiser/third-party-impr-pels to not fire.
   * Note, the call to /advertiser/pixel has already happened
   *
   * The sole purpose of postMessageReceiver is to listen for a message
   * fired by third_party_impr_pels upon load.  When we receive this message
   * AND impression HAS been fired AND pixel HAS NOT been fired, we fire it here.
   *
   * evt sample data (partial)
   * data:"{"action":"iframe_loaded","m2m_impr_id":"30770491","componentName":"Card_1"}",
   * source:Window,
   * target:Window,
   * origin:"https://matt-engagement.inmarket.com"
   *
   */
  postMessageReceiver = evt => {
    // Do we trust the sender of this message?
    // (might be from another host, for example).
    // Check if the message is coming from a DFP ad unit without PELS.
    const stringData = typeof evt.data === 'object' ? JSON.stringify(evt.data) : evt.data;
    if (typeof stringData !== 'string' || stringData.indexOf('react-devtools') !== -1) {
      return;
    }

    if (
      !evt.data ||
      (!stringData.match(/"m2m_impr_id"/) && stringData.indexOf('iframe_loaded_dfp') === -1)
    ) {
      window.server.log({
        fn: 'Card.postMessageReceiver',
        tag: 'CARD_MSG_INVALID',
        msg: `Not processing postMessage. Invalid origin: ${evt.origin}`,
        evt,
        inmarketParams: {
          componentName: this.props.componentName,
          m2m_impr_id: this.props.ad.impression_id,
          m2m_relsno: this.props.ad.m2m_relsno,
        },
      });
      console.log('Card.postMessageReceiver - returning invalid origin');
      return;
    }

    let data = {};

    try {
      // 'data' is a custom object created in the postMessage sender in third_party_impr_pel.js
      // JSON.parse will throw an exception if the data is invalid json and trigger excessive logging.
      // google calls postMessage with invalid json mulitple times, so don't parse them if they do.
      if (stringData.indexOf('goog_') === -1 && typeof evt.data.googMsgType === 'undefined') {
        try {
          data = JSON.parse(stringData);
        } catch (e) {
          if (stringData !== '{}' && stringData !== '') {
            window.server.log({
              msg: 'invalid json detected',
              tag: 'CARD_JSON_INVALID',
              data: evt.data,
              inmarketParams: {
                componentName: this.props.componentName,
                m2m_impr_id: this.props.ad.impression_id,
                m2m_relsno: this.props.ad.m2m_relsno,
              },
            });
          }
        }
      } else {
        console.log('Card.postMessageReceiver - returning looks like google');
        return;
      }

      console.log('Card.postMessageReceiver - message accepted');

      if (data.action === 'notify_app_opened') {
        // sent from the cmp pages, so that we know that to notify
        // when the app is open... the cmp tags render pretty much
        // nothing until the app_opened event is sent.
        console.log('Card.postMessageReceiver - setState notifyAppOpened:true');
        this.sendAppOpenedEvent();

        // make sure that the pixel fires regardless of whether it reports back to us
        setTimeout(this.handlePixelFire, 500);

        return;
      }

      if (data.action === 'moat_pixel_update') {
        return;
      }

      // We test for 'iframe_loaded' which indicates that we are sending the message.
      // Since we are using postMessage Card_0 will hear the message intended for Card_1
      // and vice versa.  For this reason we have to make sure the message is for this card
      // so we test for impression_id and componentName (value can be Card_0, Card_1, etc)
      let { m2m_impr_id } = data;
      if (Array.isArray(m2m_impr_id)) {
        m2m_impr_id = data.m2m_impr_id[0];
      }

      const loadedDFP = data.action === 'iframe_loaded_dfp';
      const loadedCreative =
        m2m_impr_id === this.props.ad.impression_id &&
        data.action === 'iframe_loaded' &&
        (!data.componentName || data.componentName === this.props.componentName);

      if (loadedCreative) {
        console.log(`Card.postMessageReceiver() received message from ${evt.origin}`, evt);
        this.iframeLoaded = true;
        console.log(`Card.postMessageReceiver() - this.iframeLoaded = true`);

        this.setState({ thirdPartyImprPelTarget: evt.source });

        if (this.state.click_through === false) {
          this.setState({ click_through: true });
        }

        // 'firedPixels' is set if impression_dtime was updated by calling advertiser/pixel
        // 'thirdPartyPixelFired' the advertiser/third-party-impr-pels has not been called
        if (this.firedPixels && !this.thirdPartyPixelFired) {
          // if we *have not* already fired the third party pixel
          // but *have* fired the impression
          // go ahead and fire it now
          this.fireInternalPixel();

          // log that we have fired the pixel
          window.server.log({
            fn: 'Card.postMessageReceiver',
            tag: 'CARD_FIRED',
            msg: 'Fired pixel',
            data,
            origin: evt.origin,
            inmarketParams: {
              componentName: this.props.componentName,
              m2m_impr_id: this.props.ad.impression_id,
              m2m_relsno: this.props.ad.m2m_relsno,
            },
            stateFiredPixels: this.firedPixels,
            stateThirdPartyPixelFired: this.thirdPartyPixelFired,
          });
        } else if (typeof data.googMsgType === 'undefined') {
          // we're not interested in postMessages sent from google, so don't log
          // "data":{"googMsgType":"adpnt"},

          window.server.log({
            fn: 'Card.postMessageReceiver',
            tag: 'CARD_FIRE_AMBIG',
            msg: 'We think pixel already fired, or we havent yet updated impression_dtime.',
            data,
            origin: evt.origin,
            inmarketParams: {
              componentName: this.props.componentName,
              m2m_impr_id: this.props.ad.impression_id,
              m2m_relsno: this.props.ad.m2m_relsno,
            },
            stateFiredPixels: this.firedPixels,
            stateThirdPartyPixelFired: this.thirdPartyPixelFired,
          });
        }

        if (this.iframeError === true) {
          this.iframeError = false;
          this.logIframeErrorMisfire(evt.origin);
        }
      } else {
        // Post Message from DFP ad without PELS, used for iframe error handling.
        if (data.action === 'iframe_loaded_dfp') {
          this.iframeLoaded = true;
          if (this.iframeError === true) {
            this.iframeError = false;
            this.logIframeErrorMisfire(evt.origin);
          }
          // iframe_pixels_fired is handled somewhere else, so don't log this as a false message
        } else if (data.action !== 'iframe_pixels_fired') {
          // we are not firing the pixel so do some logging to the server
          const theMessage = `m2m_impr_id === this.props.ad.impression_id: ${m2m_impr_id ===
            this.props.ad.impression_id}
   data.action === 'iframe_loaded': ${data.action === 'iframe_loaded'}
   !data.componentName: ${!data.componentName}
   data.componentName === this.props.componentName: ${data.componentName ===
     this.props.componentName}
   `;
          window.server.log({
            fn: 'Card.postMessageReceiver',
            tag: 'CARD_FIRE_SKIP',
            msg: `Unable to fire pixel. Message does not match Card. ${theMessage}`,
            data,
            origin: evt.origin,
            inmarketParams: {
              componentName: this.props.componentName,
              m2m_impr_id: this.props.ad.impression_id,
              m2m_relsno: this.props.ad.m2m_relsno,
            },
            stateFiredPixels: this.firedPixels,
            stateThirdPartyPixelFired: this.thirdPartyPixelFired,
          });
        }
      }
    } catch (err) {
      console.log('Card.postMessageReceiver error: ', err);

      // we have caught an exception so log it to the server
      window.server.log({
        fn: 'Card.postMessageReceiver',
        tag: 'CARD_FIRE_EXCEPT',
        msg: `Caught exception: ${err.message}`,
        data: evt.data,
        origin: evt.origin,
        inmarketParams: {
          m2m_relsno: this.props.ad.m2m_relsno,
          m2m_impr_id: this.props.ad.impression_id,
          componentName: this.props.componentName,
        },
        stateFiredPixels: this.firedPixels,
        stateThirdPartyPixelFired: this.thirdPartyPixelFired,
      });
    }
    this.focusWindow();
  };

  inViewport = () => {
    const maxWidth = window.innerWidth || window.screen.width;
    const { maxHeight } = this.state;
    const cardSize = this.cardIframe.getBoundingClientRect
      ? this.cardIframe.getBoundingClientRect()
      : false;
    let inViewport = false;
    if (cardSize) {
      inViewport = !!(cardSize.width + cardSize.left <= maxWidth && cardSize.height <= maxHeight);
    }
    return {
      vp: {
        w: maxWidth,
        h: window.innerHeight || window.screen.height,
      },
      inViewport,
      ad: {
        w: cardSize.width,
        h: cardSize.height,
      },
    };
  };

  postMessageSender = pixelHtml => {
    const { ad, componentName } = this.props;
    const { m2m_relsno, impression_id } = ad;

    if (!this.state.thirdParPelSrc) {
      console.log('Unable to fire pixel: thirdPartyImprPelTarget is undefined');
      window.server.log({
        fn: 'Card.postMessageSender',
        tag: 'CARD_MSG_FIRE_IFR_FAIL',
        msg: 'Unable to fire pixel: thirdPartyImprPelTarget is undefined',
        inmarketParams: {
          m2m_relsno,
          m2m_impr_id: impression_id,
          componentName,
        },
      });

      this.thirdPartyPixelFired = false;
      return false;
    }

    const { percentViewable } = this.state;

    window.server.log({
      tag: 'CARD_MSG_ENT',
      msg: 'Entered postMessageSender',
      inmarketParams: {
        m2m_relsno,
        m2m_impr_id: impression_id,
        componentName,
        pixelHtml,
      },
    });

    const send = {
      action: 'fire_pixel',
      m2m_impr_id: impression_id,
      componentName,
      pixelHtml,
    };

    console.log(`Card.postMessageSender(): initiating third party pixel fire`, send);

    this.state.thirdParPelSrc.postMessage(JSON.stringify(send), '*');

    window.server.log({
      fn: 'Card.postMessageSender',
      tag: 'CARD_MSG_FIRED',
      msg: 'Did fire pixel',
      inmarketParams: {
        m2m_relsno,
        m2m_impr_id: impression_id,
        componentName,
      },
      meta_data: {
        percentViewable: Math.round(percentViewable),
        inViewport: this.inViewport(),
      },
    });

    this.thirdPartyPixelFired = true;
  };

  cardLoadedInView = () => {
    console.log(
      `this.props.open === true`,
      this.props.open === true,
      `this.iframeLoaded === true`,
      this.iframeLoaded === true,
      `!this.firePixelExec`,
      !this.firePixelExec,
      `(this.props.didFinishAnimation || this.cardInViewport())`,
      this.props.didFinishAnimation || this.cardInViewport(),
      ` this.props.didFinishAnimation`,
      this.props.didFinishAnimation,
      `this.cardInViewport()`,
      this.cardInViewport()
    );
    return (
      this.props.open === true &&
      this.iframeLoaded === true &&
      !this.firePixelExec &&
      (this.props.didFinishAnimation || this.cardInViewport())
    );
  };

  postE2IFrameScale(from, targets) {
    if (get(this, 'cardIframe.current.contentWindow')) {
      const payload = {
        e2IframeScale: this.state.frameScale,
        left: this.state.left,
        width: this.state.width,
        top: Browser.getTopPadding(),
      };
      console.log(`Card - posting e2IframeScale (from ${from}):`, payload, targets);
      this.cardIframe.current.contentWindow.postMessage(payload, '*');

      if (targets) {
        for (const tgt of targets) {
          tgt.postMessage(payload, '*');
        }
      }
    }
  }

  render() {
    let { scrollable } = this.props.ad;
    scrollable = scrollable === 'false' ? false : scrollable;

    const {
      iframeSrc,
      borderColor,
      iframeContents,
      finalPositionX,
      backgroundColor,
      openCeltra,
      height,
      frameScale,
      marginBottom,
      borderWidth,
      width,
      left,
      top,
      wrapperHeight,
      maxWidth,
      maxHeight,
      wrapperDisplay,
      loadingJson,
      cardTransitionDuration,
      mouse,
      isPressed,
      springConfig: [s0, d0],
    } = this.state;

    const cardStyle = {
      backgroundColor,
      borderColor,
      borderStyle: 'solid',
      borderWidth,
      transitionProperty: 'width height',
      WebkitTransitionProperty: 'width height',
      transitionDuration: '500ms',
      WebkitTransitionDuration: '500ms',

      width: '100vw', // Something to check for viewability
      height: '100vh',

      // width: this.state.width,
      // height: this.state.maxHeight > this.state.height ? '100vh' : `${this.state.height}px`,
      marginBottom,
      position: 'relative',
      zIndex: 400,
      top: 0,

      WebkitTapHighlightColor: 'rgba(0,0,0,0)',
      WebkitTouchCallout: 'none',
      MozUserSelect: 'none',
      WebkitUserSelect: 'none',
      maxHeight: `${maxHeight}`,
      maxWidth: `${maxWidth}`,
      overflowY: scrollable ? 'scroll' : 'hidden',
      WebkitOverflowScrolling: scrollable ? 'touch' : 'auto',
    };

    const iframeBlockerStyle = {
      backgroundColor: 'rgba(0, 0, 0, 0)',
      height,
      width,

      transitionProperty: 'width height',
      WebkitTransitionProperty: 'width height',
      transitionDuration: '500ms',
      WebkitTransitionDuration: '500ms',
      maxHeight: `${maxHeight}`,
      maxWidth: `${maxWidth}`,

      position: 'relative',
      left: 0,
      bottom: 0,
      right: 0,
      display: 'none',
      cursor: 'pointer',
      zIndex: 900,
    };

    const iframeStyle = {
      overflow: 'visible',
      width: `100vw`,
      position: 'absolute',
      left: 0,
      MozTransition: '500ms',
      WebkitTransition: '500ms',
      transition: '500ms',
      height: window.innerHeight - (Browser.getTopPadding() + Browser.getHeaderHeight()),
      top: Browser.getTopPadding() + Browser.getHeaderHeight(),
    };

    const stiffness = s0 * 3;
    const damping = d0 * 2;
    const motionStyle = isPressed
      ? { x: mouse[0], y: mouse[1] }
      : {
          x: spring(finalPositionX, [stiffness, damping]),
          y: spring(0, [stiffness, damping]),
        };

    const { ad, componentName, cardViewState } = this.props;
    return (
      <div>
        <Motion style={motionStyle}>
          {({ x, y }) => (
            <div
              style={{
                transform: `translate3d(${x}px, 0, 0)`,
                WebkitTransform: `translate3d(${x}px, 0, 0)`,
              }}
            >
              <div
                style={{
                  display: `${wrapperDisplay}`,
                  height: `${wrapperHeight}px`,
                  transitionProperty: `height padding-top`,
                  WebkitTransitionProperty: `height padding-top`,
                  transitionDuration: `${cardTransitionDuration}`,
                  WebkitTransitionDuration: `${cardTransitionDuration}`,
                  maxHeight: `${maxHeight}`,
                  maxWidth: `${maxWidth}`,
                  top: `${top}`,
                }}
              >
                {!loadingJson && this.props.open && (
                  <Viewer
                    celtraFixes={{
                      e2IframeScale: frameScale,
                      left,
                      width,
                      top: Browser.getTopPadding(),
                    }}
                    cardViewState={cardViewState}
                    openCeltra={openCeltra}
                    sendAppOpenedEvent={this.sendAppOpenedEvent}
                    type={get(iframeContents, 'type')}
                    firedPixels={this.firedPixels}
                    iframeContents={iframeContents}
                    iframeSrc={iframeSrc}
                    cardStyle={cardStyle}
                    iframeBlockerStyle={iframeBlockerStyle}
                    iframeStyle={iframeStyle}
                    ad={ad}
                    componentName={componentName}
                    didFinishLoad={this.didFinishLoad}
                    logTagLoadError={this.logTagLoadError}
                    cardIframe={this.cardIframe}
                  />
                )}
              </div>
            </div>
          )}
        </Motion>
      </div>
    );
  }
}

export default CardView;
